import type { AiRequestDescriptor } from './ai-request-descriptor.types';
import { ContextualRequest } from './context';
import { SuggestionsResponse } from './ai-suggestions.types';
import { CanvasCloseCommand, CanvasStatusCommand, CanvasUpdateCommand } from './canvas-command.types';

export interface PromptRequest extends ContextualRequest {
    user_message: string;
    thread_id: string | null;
}

export interface PromptResponse {
    commands: ResponseCommand[];
}

export interface PollResult {
    isDone: boolean;
    response: PromptResponse;
}

export interface StreamResponse {
    isDone: boolean;
    message: PromptResponse;
}

export interface RawStreamResponse {
    isDone: boolean;
    message: string;
}

export type ResponseCommand =
    | SessionInitCommand
    | UserInteractCommand
    | InstructionCommand
    | ToolStartCommand
    | ToolCompleteCommand
    | ActionsSuggestionsCommand
    | SuggestionsResponse
    | CanvasStatusCommand
    | CanvasCloseCommand
    | CanvasUpdateCommand;

export interface SessionInitCommand {
    type: 'session_init';
    session_id: string;
}

export interface UserInteractCommand {
    type: 'user_interact';
    message: string;
    messageId: string;
}

export interface InstructionCommand {
    type: 'instruction';
    data: InstructionCommandData;
}

// camelCase variant (success_center routes)
interface ToolStartCommandCamelCase {
    type: 'tool_start';
    sessionId: string;
    toolName: string;
    toolInput: string;
}

// snake_case variant (script_builder/TTT routes)
interface ToolStartCommandSnakeCase {
    type: 'tool_start';
    session_id: string;
    tool_name: string;
    tool_input: string;
}

export type ToolStartCommand = ToolStartCommandCamelCase | ToolStartCommandSnakeCase;

export interface ToolCompleteCommand {
    type: 'tool_complete';
    sessionId: string;
    tool_result: string;
}

export interface ActionsSuggestionsCommand {
    type: 'actions_suggestions';
    actions_suggestions: string[];
}

export type InstructionCommandData = Record<string, unknown>;

export type AiPromptDescriptor = AiRequestDescriptor<'POST', `start_fetch_llm_response`, PromptRequest>;

export type AiStreamDescriptor = AiRequestDescriptor<'POST', `llm_response`, string>;

export function isToolStartCommand(command: ResponseCommand): command is ToolStartCommand {
    return command.type === 'tool_start';
}

export function getToolStartName(command: ToolStartCommand): string {
    return 'toolName' in command ? command.toolName : command.tool_name;
}

export function getToolStartInput(command: ToolStartCommand): string {
    return 'toolInput' in command ? command.toolInput : command.tool_input;
}

export function isToolCompleteCommand(command: ResponseCommand): command is ToolCompleteCommand {
    return command.type === 'tool_complete';
}

export function findToolStartCommand(commands: ResponseCommand[], predicate: (name: string) => boolean): ToolStartCommand | undefined {
    return commands.filter(isToolStartCommand).find((cmd) => predicate(getToolStartName(cmd)));
}
